import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Folder, FileText, ChevronRight, Search, Clock, Star, PenLine, CheckCircle2,
  AlertTriangle, Building2, Truck, Wallet, Receipt, LayoutTemplate, User, Loader2,
  ExternalLink, Home, Plus, UploadCloud, FileSpreadsheet, MonitorPlay, Sparkles,
  MoreVertical, Eye, Download, Trash2, ArrowUp, ArrowDown, Pencil, GripVertical, FolderUp,
} from "lucide-react";

const ICONS = {
  user: User, building: Building2, truck: Truck, wallet: Wallet, receipt: Receipt,
  layout: LayoutTemplate, clock: Clock, star: Star, pen: PenLine, check: CheckCircle2,
  alert: AlertTriangle,
};
const STATUS_TINT = {
  ISSUED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  ACCEPTED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  SENT: "bg-blue-50 text-blue-700 border-blue-200",
  VIEWED: "bg-blue-50 text-blue-700 border-blue-200",
  "AWAITING SIGNATURE": "bg-amber-50 text-amber-700 border-amber-200",
  REVIEW: "bg-amber-50 text-amber-700 border-amber-200",
  DRAFT: "bg-slate-100 text-slate-500 border-slate-200",
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function OneFiles() {
  const initialPath = (() => {
    try { return new URLSearchParams(window.location.search).get("path") || ""; }
    catch { return ""; }
  })();
  const [tree, setTree] = useState({ roots: [], summary: {} });
  const [path, setPath] = useState(initialPath);
  const [data, setData] = useState({ folders: [], files: [], breadcrumbs: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [wsModal, setWsModal] = useState(null); // {kind} — presentation only
  const [aiModal, setAiModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef(null);
  const folderRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const navigate = useNavigate();
  const [menuFor, setMenuFor] = useState(null);

  useEffect(() => { api.get("/one-files/tree").then((r) => setTree(r.data)).catch(() => {}); }, []);

  const browse = useCallback(async (p, query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/one-files/browse", { params: { path: p, q: query } });
      setData(data);
    } catch { setData({ folders: [], files: [], breadcrumbs: [], total: 0 }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { browse(path, q); }, [path]); // eslint-disable-line

  const OFFICE_TYPES = ["DOCX", "DOC", "XLSX", "XLS", "PPTX", "PPT", "CSV"];

  const fetchBlob = async (f) => {
    const rel = (f.open_url || "").replace(/^\/api/, "");
    if (!rel) return null;
    const { data } = await api.get(rel, { responseType: "blob" });
    return data;
  };

  const triggerDownload = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name || "document"; document.body.appendChild(a);
    a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const openFile = async (f) => {
    setMenuFor(null);
    if (f.native && f.edit_id) { navigate(`/files/editor/${f.edit_id}`); return; }
    if (!f.open_url) return;
    // Office formats can't render inline in a browser tab → download instead of
    // showing a broken viewer ("Sorry, we couldn't open the file").
    if (OFFICE_TYPES.includes((f.type || "").toUpperCase())) { return downloadFile(f); }
    try {
      const blob = await fetchBlob(f);
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) triggerDownload(blob, f.name);           // popup blocked → save it
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Couldn't open the file. Please try again."); }
  };

  const downloadFile = async (f) => {
    setMenuFor(null);
    try {
      const blob = await fetchBlob(f);
      if (blob) triggerDownload(blob, f.name);
    } catch { toast.error("Download failed. Please try again."); }
  };

  const editFile = (f) => {
    setMenuFor(null);
    if (f.native && f.edit_id) { navigate(`/files/editor/${f.edit_id}`); return; }
    toast.message("Editing this file", { description: "Downloading a copy — edit it locally, then upload the new version." });
    downloadFile(f);
  };

  const renameFile = async (f) => {
    setMenuFor(null);
    const next = window.prompt("Rename file", f.name || "");
    if (next === null) return;
    const name = next.trim();
    if (!name || name === f.name) return;
    try {
      await api.patch(`/one-files/document/${f.id}`, { name });
      toast.success("File renamed");
      browse(path, q);
    } catch { toast.error("Couldn't rename this file."); }
  };

  const deleteFile = async (f) => {
    setMenuFor(null);
    if (!window.confirm(`Delete "${f.name}"? This can be restored by an administrator.`)) return;
    try {
      await api.delete(`/one-files/document/${f.id}`);
      toast.success("File deleted");
      browse(path, q);
    } catch { toast.error("Couldn't delete this file."); }
  };

  const moveFile = async (f, dir) => {
    const list = (data.files || []);
    const reord = list.filter((x) => x.reorderable);
    const i = reord.findIndex((x) => x.id === f.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= reord.length) return;
    const next = [...reord];
    [next[i], next[j]] = [next[j], next[i]];
    // optimistic reorder (reorderable rows first, others keep their place)
    setData((d) => ({ ...d, files: [...next, ...list.filter((x) => !x.reorderable)] }));
    setMenuFor(null);
    try { await api.post("/one-files/reorder", { ordered_ids: next.map((x) => x.id) }); }
    catch { toast.error("Couldn't save the new order."); browse(path, q); }
  };

  const doUpload = async (fileObj) => {
    if (!fileObj) return;
    setUploading(true); setMenuOpen(false);
    const fd = new FormData();
    fd.append("file", fileObj);
    fd.append("folder_path", path || "my-files");
    if (data.context_customer) fd.append("customer", data.context_customer);
    try {
      const { data: res } = await api.post("/one-files/upload", fd);
      toast.success(res.possible_duplicate ? "Uploaded (possible duplicate detected)" : "File uploaded to ONE FILES");
      browse(path, q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const doFolderUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.size > 0 && f.size <= 40 * 1024 * 1024);
    if (!files.length) { toast.error("No uploadable files in that folder"); return; }
    setUploading(true); setMenuOpen(false);
    const base = path || "my-files";
    let ok = 0, fail = 0;
    const t = toast.loading(`Uploading folder — 0/${files.length}…`);
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const rel = f.webkitRelativePath || f.name;
      const dir = rel.includes("/") ? rel.slice(0, rel.lastIndexOf("/")) : "";
      const folder_path = dir ? `${base}/${dir}` : base;
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder_path", folder_path);
      if (data.context_customer) fd.append("customer", data.context_customer);
      try { await api.post("/one-files/upload", fd); ok++; } catch { fail++; }
      toast.loading(`Uploading folder — ${i + 1}/${files.length}…`, { id: t });
    }
    toast.dismiss(t);
    if (fail) toast.warning(`Folder uploaded — ${ok} file(s), ${fail} failed`);
    else toast.success(`Folder uploaded — ${ok} file(s)`);
    browse(path, q);
    setUploading(false);
  };

  const dropReorder = async (targetId) => {
    setDragOverId(null);
    const fromId = dragId; setDragId(null);
    if (!fromId || fromId === targetId) return;
    const list = (data.files || []);
    const reord = list.filter((x) => x.reorderable);
    const from = reord.findIndex((x) => x.id === fromId);
    const to = reord.findIndex((x) => x.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...reord];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setData((d) => ({ ...d, files: [...next, ...list.filter((x) => !x.reorderable)] }));
    try { await api.post("/one-files/reorder", { ordered_ids: next.map((x) => x.id) }); }
    catch { toast.error("Couldn't save the new order."); browse(path, q); }
  };

  const createDoc = async (kind) => {
    setMenuOpen(false);
    if (kind === "PRESENTATION") { setWsModal({ kind }); return; }
    if (kind === "AI") { setAiPrompt(""); setAiModal(true); return; }
    // DOCUMENT | SPREADSHEET → native in-app editor (no external server)
    setCreating(true);
    try {
      const { data: res } = await api.post("/one-files/native", {
        native_type: kind,
        folder_path: path || "my-files",
        customer: data?.context_customer || undefined,
      });
      navigate(`/files/editor/${res.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the document"); }
    finally { setCreating(false); }
  };

  const createWithAi = async () => {
    if (!aiPrompt.trim()) { toast.error("Describe the document first"); return; }
    setAiBusy(true);
    try {
      const { data: res } = await api.post("/one-files/native/ai", {
        native_type: "DOCUMENT", title: aiPrompt.trim(), folder_path: path || "my-files",
      });
      setAiModal(false);
      navigate(`/files/editor/${res.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "ONE AI could not draft this"); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="flex h-[calc(100vh-56px)]" data-testid="one-files-page">
      {/* quick access */}
      <aside className="w-60 shrink-0 border-r border-slate-200 bg-slate-50/60 p-3 overflow-y-auto">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 px-2 mb-1">Quick access</div>
        <button onClick={() => setPath("")} data-testid="qa-home"
          className={"w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm " + (path === "" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}>
          <Home className="h-4 w-4" /> Home
        </button>
        {(tree.roots || []).map((r) => {
          const Icon = ICONS[r.icon] || Folder;
          const active = path === r.key || path.startsWith(r.key + "/");
          return (
            <button key={r.key} onClick={() => setPath(r.key)} data-testid={`qa-${r.key}`}
              className={"w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm mt-0.5 " + (active ? "bg-slate-200 text-slate-900" : "text-slate-700 hover:bg-slate-100")}>
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate flex-1 text-left">{r.label}</span>
              {r.count ? <span className="text-[10px] text-slate-400">{r.count}</span> : null}
            </button>
          );
        })}
      </aside>

      {/* main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-1 text-sm text-slate-500 min-w-0 flex-1 overflow-x-auto">
            {(data.breadcrumbs || [{ key: "", label: "ONE FILES" }]).map((b, i, arr) => (
              <span key={b.key} className="flex items-center gap-1 whitespace-nowrap">
                <button onClick={() => setPath(b.key)} data-testid={`crumb-${i}`}
                  className={"hover:text-slate-900 " + (i === arr.length - 1 ? "font-semibold text-slate-900" : "")}>{b.label}</button>
                {i < arr.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
              </span>
            ))}
          </div>
          <div className="relative w-64">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && browse(path, q)}
              placeholder="Search files…" className="pl-8 h-9" data-testid="files-search" />
          </div>
          <input ref={uploadRef} type="file" hidden data-testid="files-upload-input"
            onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ""; }} />
          <input ref={folderRef} type="file" hidden webkitdirectory="" directory="" multiple
            data-testid="files-folder-input"
            onChange={(e) => { doFolderUpload(e.target.files); e.target.value = ""; }} />
          <div className="relative">
            <Button onClick={() => setMenuOpen((v) => !v)} data-testid="create-menu-btn"
              className="bg-slate-900 hover:bg-slate-800 text-white h-9">
              {(uploading || creating) ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Create
            </Button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-1 w-60 z-50 rounded-xl border border-slate-200 bg-white shadow-lg py-1" data-testid="create-menu">
                  {[
                    { k: "DOCUMENT", label: "Document", icon: FileText },
                    { k: "SPREADSHEET", label: "Spreadsheet", icon: FileSpreadsheet },
                    { k: "PRESENTATION", label: "Presentation", icon: MonitorPlay },
                  ].map((o) => (
                    <button key={o.k} onClick={() => createDoc(o.k)} data-testid={`create-${o.k.toLowerCase()}`}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                      <o.icon className="h-4 w-4 text-slate-500" /> {o.label}
                    </button>
                  ))}
                  <button onClick={() => createDoc("AI")} data-testid="create-ai"
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-violet-700 hover:bg-violet-50">
                    <Sparkles className="h-4 w-4" /> Create with AI
                  </button>
                  <div className="h-px bg-slate-100 my-1" />
                  <button onClick={() => { setMenuOpen(false); uploadRef.current?.click(); }} data-testid="create-upload"
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    <UploadCloud className="h-4 w-4 text-slate-500" /> Upload File
                  </button>
                  <button onClick={() => { setMenuOpen(false); folderRef.current?.click(); }} data-testid="create-upload-folder"
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    <FolderUp className="h-4 w-4 text-slate-500" /> Upload Folder
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="py-20 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : (
            <>
              {(data.folders || []).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                  {data.folders.map((f) => (
                    <button key={f.key} onClick={() => setPath(f.key)} data-testid={`folder-${f.key}`}
                      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-slate-300 hover:shadow-sm text-left">
                      <div className="h-9 w-9 rounded-lg bg-amber-50 grid place-items-center">
                        <Folder className="h-5 w-5 text-amber-500" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{f.label}</div>
                        {f.smart && <span className="text-[10px] text-slate-400">smart folder</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {(data.files || []).length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white">
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <div className="col-span-5">Name</div>
                    <div className="col-span-3">Customer / Entity</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2 text-right">Modified</div>
                  </div>
                  {data.files.map((f) => {
                    const canReorder = f.reorderable;
                    const reord = (data.files || []).filter((x) => x.reorderable);
                    const rIdx = reord.findIndex((x) => x.id === f.id);
                    return (
                    <div key={f.id + f.kind} data-testid={`file-${f.id}`}
                      draggable={canReorder}
                      onDragStart={canReorder ? (e) => { setDragId(f.id); e.dataTransfer.effectAllowed = "move"; } : undefined}
                      onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                      onDragOver={canReorder ? (e) => { e.preventDefault(); if (dragId && dragId !== f.id) setDragOverId(f.id); } : undefined}
                      onDragLeave={canReorder ? () => setDragOverId((v) => (v === f.id ? null : v)) : undefined}
                      onDrop={canReorder ? (e) => { e.preventDefault(); dropReorder(f.id); } : undefined}
                      onClick={() => openFile(f)}
                      className={"group relative grid grid-cols-12 gap-2 px-4 py-2.5 items-center border-b border-slate-50 hover:bg-slate-50 cursor-pointer "
                        + (dragOverId === f.id ? "ring-2 ring-inset ring-slate-900/40 bg-slate-50 " : "")
                        + (dragId === f.id ? "opacity-40 " : "")}>
                      <div className="col-span-5 flex items-center gap-2 min-w-0">
                        {canReorder && (
                          <span data-testid={`file-grip-${f.id}`} onClick={(e) => e.stopPropagation()}
                            title="Drag to reorder"
                            className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 -ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <GripVertical className="h-4 w-4" />
                          </span>
                        )}
                        {f.native_type === "SPREADSHEET"
                          ? <FileSpreadsheet className="h-4 w-4 text-emerald-500 shrink-0" />
                          : <FileText className="h-4 w-4 text-slate-400 shrink-0" />}
                        <span className="text-sm text-slate-800 truncate">{f.name}</span>
                        {f.native ? <PenLine className="h-3 w-3 text-slate-300" />
                          : f.open_url && <ExternalLink className="h-3 w-3 text-slate-300" />}
                      </div>
                      <div className="col-span-3 text-[12px] text-slate-500 truncate">{f.customer || "—"}</div>
                      <div className="col-span-2">
                        <Badge className={"text-[10px] border " + (STATUS_TINT[f.status] || STATUS_TINT.DRAFT)}>{f.status}</Badge>
                      </div>
                      <div className="col-span-2 flex items-center justify-end gap-2">
                        <span className="text-[12px] text-slate-400 hidden sm:inline">{fmt(f.modified)}</span>
                        <div className="relative">
                          <button
                            data-testid={`file-actions-${f.id}`}
                            onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === f.id ? null : f.id); }}
                            className="p-1 rounded-md text-slate-400 hover:bg-slate-200 hover:text-slate-700">
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {menuFor === f.id && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setMenuFor(null); }} />
                              <div className="absolute right-0 top-7 z-50 w-40 rounded-lg border border-slate-200 bg-white shadow-lg py-1"
                                data-testid={`file-menu-${f.id}`} onClick={(e) => e.stopPropagation()}>
                                <button data-testid={`file-view-${f.id}`} onClick={() => openFile(f)}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50">
                                  <Eye className="h-3.5 w-3.5 text-slate-400" /> View
                                </button>
                                <button data-testid={`file-edit-${f.id}`} onClick={() => editFile(f)}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50">
                                  <PenLine className="h-3.5 w-3.5 text-slate-400" /> Edit
                                </button>
                                {canReorder && (
                                  <button data-testid={`file-rename-${f.id}`} onClick={() => renameFile(f)}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50">
                                    <Pencil className="h-3.5 w-3.5 text-slate-400" /> Rename
                                  </button>
                                )}
                                {!f.native && (
                                  <button data-testid={`file-download-${f.id}`} onClick={() => downloadFile(f)}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50">
                                    <Download className="h-3.5 w-3.5 text-slate-400" /> Download
                                  </button>
                                )}
                                {canReorder && (
                                  <>
                                    <button data-testid={`file-up-${f.id}`} disabled={rIdx <= 0} onClick={() => moveFile(f, -1)}
                                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                                      <ArrowUp className="h-3.5 w-3.5 text-slate-400" /> Move up
                                    </button>
                                    <button data-testid={`file-down-${f.id}`} disabled={rIdx < 0 || rIdx >= reord.length - 1} onClick={() => moveFile(f, 1)}
                                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                                      <ArrowDown className="h-3.5 w-3.5 text-slate-400" /> Move down
                                    </button>
                                  </>
                                )}
                                {canReorder && (
                                  <button data-testid={`file-delete-${f.id}`} onClick={() => deleteFile(f)}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-rose-600 hover:bg-rose-50 border-t border-slate-100">
                                    <Trash2 className="h-3.5 w-3.5" /> Delete
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}

              {(data.folders || []).length === 0 && (data.files || []).length === 0 && (
                <div className="py-16 text-center" data-testid="files-empty">
                  <Folder className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <div className="text-sm text-slate-500 mb-5">
                    {data.empty_hint || "This folder is empty."}
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Button variant="outline" onClick={() => createDoc("DOCUMENT")} data-testid="empty-create-doc">
                      <FileText className="h-4 w-4 mr-1 text-slate-500" /> Document
                    </Button>
                    <Button variant="outline" onClick={() => createDoc("SPREADSHEET")} data-testid="empty-create-sheet">
                      <FileSpreadsheet className="h-4 w-4 mr-1 text-slate-500" /> Spreadsheet
                    </Button>
                    <Button onClick={() => uploadRef.current?.click()} data-testid="empty-upload"
                      className="bg-slate-900 hover:bg-slate-800 text-white">
                      <UploadCloud className="h-4 w-4 mr-1" /> Upload File
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog open={!!wsModal} onOpenChange={(o) => !o && setWsModal(null)}>
        <DialogContent className="max-w-md" data-testid="workspace-setup-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MonitorPlay className="h-4 w-4 text-amber-600" /> New presentation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <Badge className="bg-amber-50 text-amber-700 border border-amber-200">ONE WORKSPACE · SETUP REQUIRED</Badge>
            <p className="text-sm text-slate-600">
              Native <strong>Documents</strong> and <strong>Spreadsheets</strong> work right now — no setup needed.
              Full <strong>presentation</strong> editing needs an <strong>OnlyOffice Docs server</strong> connected to SUNTRONIX ONE.
            </p>
            <p className="text-sm text-slate-600">
              Meanwhile you can create a Document, or <strong>Upload File</strong> to store an existing presentation.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setWsModal(null)}>Close</Button>
              <Button variant="outline" onClick={() => { setWsModal(null); createDoc("DOCUMENT"); }}
                data-testid="modal-create-doc-instead">
                <FileText className="h-4 w-4 mr-1" /> New document
              </Button>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white"
                onClick={() => { setWsModal(null); uploadRef.current?.click(); }} data-testid="modal-upload-instead">
                <UploadCloud className="h-4 w-4 mr-1" /> Upload a file
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={aiModal} onOpenChange={(o) => !o && setAiModal(false)}>
        <DialogContent className="max-w-md" data-testid="ai-create-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-600" /> Create with ONE AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-600">
              Describe the document you need and ONE AI will draft it. You can edit everything afterwards.
            </p>
            <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={4}
              data-testid="ai-prompt-input" autoFocus
              placeholder="e.g. A professional AMC service agreement for a 50-laptop office, or a quotation cover letter for ABC Global…"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300 resize-none" />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setAiModal(false)}>Cancel</Button>
              <Button className="bg-violet-600 hover:bg-violet-700 text-white"
                onClick={createWithAi} disabled={aiBusy} data-testid="ai-create-submit">
                {aiBusy ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Drafting…</>
                  : <><Sparkles className="h-4 w-4 mr-1" /> Draft document</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
